import React from 'react';
import { inject } from 'mobx-react';
import {
  HotQuestionCategory as UIHotQuestionCategory,
  HotQuestionCategoryItemProps,
} from '@shopee/chatbot-ui/rn/components/HotQuestionCategory/rn';

import { isBank, isDriverOrFood } from '@/config/core';
import { getChildInject, getTrans, isNum } from '@/utils/instantLoaded/tools';
import {
  FlexDirection,
  HotQuestionCategoryDisplayType,
  LOCK_INTENT_NEXT_PAGE_TIME,
  TRACKING_OPERATION,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_TYPE,
} from '@/utils/constants';
import { InitStoreType } from '@/types/store/init';
import { dispatchTrack } from '@/utils/tracking';
import { COUNTRY } from '@/utils/locale';
import sendBotMessageToAgent from '@/utils/sendBotMessageToAgent';
import { addDialogue, updateDialogueInfo } from '@/utils/sessionUtils';
import { handleRequest } from '@/utils';
import { cdnFile } from '@/utils/cdnFile';
import { ChatbotUIBasicProps } from '@/types/common';

import Category from './Category';
import { MAX_HOT_QUESTION_NUMBER } from '../IntentHotQuestion/constants';
import { FEATURE_TOGGLE_TYPE, featureToggle } from '@/utils/featureToggleConfig';

import './style.scss';

export interface CategoryItem {
  category_id: number;
  category_name: string;
  /** icon 名字 */
  icon_url: string;
  /** 是否是 ad-hoc */
  is_adoc_category: boolean;
  color?: string;
  isImg: boolean;
}

export type HotQuestionCategoryProps = {
  categories: Array<CategoryItem>;
  flexDirection?: FlexDirection;
  displayType?: number;
  initStore?: InitStoreType;
  agentStore?: any;
  firstInit?: any;
  sessionStore?: any;
} & ChatbotUIBasicProps;

interface IconMapValue {
  path: string;
  isImg: boolean;
}
export interface HotQuestionCategoryState {
  categories: Array<CategoryItem>;
  iconMap: Map<string, IconMapValue>;
  groupIconMap: Map<string, IconMapValue>;
}

const PREFIX = 'hot-question-category';
const ICON_COLORS = ['#EDA500', '#26AA99', '#EE4D2D', '#16449b'];
const BANK_ICON_COLORS = ['#214D7E', '#5688E1', '#EA5F00', '#DB944B'];

@inject(getChildInject('firstInit', 'initStore', 'agentStore', 'sessionStore'))
export default class HotQuestionCategory extends React.Component<HotQuestionCategoryProps, HotQuestionCategoryState> {
  currentColorIndex = 0;
  categoryContainerRef = React.createRef<HTMLDivElement>();
  isMove = false;

  // 获取下一页intent列表，如果为true，表示暂时不可以获取
  // 当前前端设置，1s后才可以进行下一次获取
  isNextIntentLock = false;

  constructor(props: HotQuestionCategoryProps) {
    super(props);

    this.state = {
      categories: props.categories.map((cat) => ({
        ...cat,
        color: this.getIconColor(cat),
      })),
      iconMap: new Map(),
      groupIconMap: new Map(),
    };

    import('./iconConfig').then((v) => {
      this.setState({ iconMap: v.IconMap });
    });
    import('./groupIconConfig').then((v) => {
      this.setState({ groupIconMap: v.GroupIconMap });
    });
  }

  getIconColor = (category: CategoryItem) => {
    const COLORS = isBank() ? BANK_ICON_COLORS : ICON_COLORS;
    const currentColor = COLORS[this.currentColorIndex % COLORS.length];

    if (category.isImg) {
      return '';
    }

    this.currentColorIndex = this.currentColorIndex + 1;

    return currentColor;
  };

  isImg = (category: CategoryItem) => {
    const isOriginIntentHotQuestion = this.props?.displayType === HotQuestionCategoryDisplayType.Old;
    const generalIcon = isOriginIntentHotQuestion
      ? this.state.iconMap.get(category.icon_url)
      : this.state.groupIconMap.get(category.icon_url);
    if (generalIcon != null) {
      return generalIcon.isImg;
    }

    return !(category.icon_url.indexOf('icon_') === 0 || category.icon_url.includes('.svg'));
  };

  handleTouchEnd = () => {
    dispatchTrack({
      operation: TRACKING_OPERATION.ACTION_SCROLL,
      section: TRACKING_SECTION[TRACKING_TYPE.CATEGORY_OPTIMISATION],
      target: TRACKING_TARGET[TRACKING_TYPE.CATEGORY_OPTIMISATION],
      data: { chat_session_id: this.props.initStore?.sessionId || '' },
    });
  };

  componentDidMount() {
    // 左右滚动 上报一次
    // 按住 然后松开 也算一次
    // 所以直接监听 touch end 事件 触发一次即上报一次 action scroll
    this.categoryContainerRef.current?.addEventListener('touchend', this.handleTouchEnd);
  }

  componentWillUnmount() {
    this.categoryContainerRef.current?.removeEventListener('touchend', this.handleTouchEnd);
  }

  handleCategoryClick = async (category: HotQuestionCategoryItemProps) => {
    const { name, id } = category;
    const { initStore, agentStore, sessionStore, isHistory } = this.props;

    import('@/utils/btnClickHandler').then(({ trackingHandler }) => {
      // TODO: HXS tracking
      // trackingHandler({
      //   trackingname: this.trackingname,
      //   trackingdata: this.trackingdata,
      // });
    });

    if (isHistory) {
      return;
    }

    // 在agent过程中，走agent逻辑
    if (agentStore.inAgent) {
      // [live-chat-split-change]
      sendBotMessageToAgent({ content: name! });
    } else {
      if (this.isNextIntentLock) {
        return;
      }

      const curPos = addDialogue({
        from: 'user',
        type: 'Msg',
        data: { content: name },
      });

      if (isNum(curPos)) {
        const params = {
          category_id: id,
          session_id: initStore!.sessionId,
        };

        this.isNextIntentLock = true;

        const reporter = (await import('@/utils/report')).default;
        reporter.apiBatchReport({ type: 'count', value: { label: 'func_head_hqs_load' }});

        handleRequest('getCategoryIntentList', params, { curPos }).then(async (res: any) => {
          res
            && updateDialogueInfo(
              {
                dialogueId: res.dialogue_id,
                dialogueType: res.dialogue_type,
              },
              curPos,
            );
          if (res) {
            const { refresh_head_intents: hotQuestions = [], refresh_shortcut_intents: shortcutIntent = [] }
              = res || {};

            if (hotQuestions?.length === MAX_HOT_QUESTION_NUMBER || !hotQuestions?.length) {
              reporter.apiBatchReport({ type: 'count', value: { label: 'func_head_hqs_load_ok' }});
            }

            if (hotQuestions?.length) {
              sessionStore.UPDATE_INTENT_HOT_QUESTION(hotQuestions);
            }

            const { shortcutPredictionAvaliable } = await import('@/utils/tools');
            if (shortcutPredictionAvaliable && shortcutIntent?.length) {
              this.props.initStore?.UPDATE_INTENT_PREDICTION_SHORTCUT_IF_ALLOWED(shortcutIntent);
            }
          }
        });
        setTimeout(() => {
          this.isNextIntentLock = false;
        }, LOCK_INTENT_NEXT_PAGE_TIME);
      }
    }
  };

  render() {
    const { categories, iconMap, groupIconMap } = this.state;
    const { firstInit, initStore, displayType, isHistory, historySessionId, historyDialogueId, dialogueId }
      = this.props;

    const getImgIconSrc = (category: CategoryItem) => {
      // 针对 tw 地区 的 spx icon 替换掉
      const spxIconUrl = 'img_shopee_express';
      const spxTwIconUrl = 'img_shopee_express_tw';
      if (COUNTRY === 'tw' && spxIconUrl === category.icon_url) {
        return groupIconMap.get(spxTwIconUrl)?.path;
      }
      return (
        displayType === HotQuestionCategoryDisplayType.Old
          ? iconMap.get(category.icon_url)
          : groupIconMap.get(category.icon_url)
      )?.path;
    };

    const isUIOptimisation = featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY, displayType) || (isHistory && isDriverOrFood());
    if (
      isUIOptimisation
    ) {
      const realDisplayType = isDriverOrFood() ? HotQuestionCategoryDisplayType.OtherFrequentlyAsked : displayType;
      const isSmallerTab = realDisplayType === HotQuestionCategoryDisplayType.SmallerTab;
      const trackingType = isSmallerTab ? TRACKING_TYPE.CATEGORY_SMALLER_TAB : TRACKING_TYPE.CATEGORY_BUBBLE_ITEM;
      const commonTrackingData = {
        chat_session_id: isHistory ? historySessionId : firstInit?.initStore?.sessionId || initStore?.sessionId || '',
        chat_dialogue_id: isHistory ? historyDialogueId : dialogueId || '',
      };

      return (
        <UIHotQuestionCategory
          categoryDisplay={realDisplayType as any}
          categoryWidth={window.screen.width}
          categoryList={categories.map((category, index) => {
            const isAdHoc = category.is_adoc_category;
            const url = category.icon_url;
            const icon = isAdHoc ? getImgIconSrc(category) || cdnFile(url) : url;
            return {
              id: category.category_id,
              name: category.category_name,
              icon,
              isAdHoc,
              trackingData: {
                trackingsection: TRACKING_SECTION[trackingType],
                trackingtarget: TRACKING_TARGET[trackingType],
                trackingdataBasic: {
                  ...commonTrackingData,
                  question_category: category.category_name,
                  location: index,
                },
              },
            };
          })}
          onCategoryClick={this.handleCategoryClick}
          adhocLabel={getTrans('ad_hoc_category_label') || 'Hot'}
          componentTrackingData={{
            smallerTab: isSmallerTab
              ? {
                trackingsection: TRACKING_SECTION[trackingType],
                trackingtarget: TRACKING_TARGET[trackingType],
                trackingdataBasic: commonTrackingData,
              }
              : undefined,
          }}
        />
      );
    }

    return (
      <div
        ref={this.categoryContainerRef}
        className={`${PREFIX} ${this.props.flexDirection === FlexDirection.Row ? `${PREFIX}_flow` : ''}`}
      >
        {categories.map((category, index) => (
          <Category
            key={category.category_id}
            index={index}
            id={category.category_id}
            label={category.category_name}
            icon={category.icon_url}
            isAdHoc={category.is_adoc_category}
            color={category.color!}
            isImg={this.isImg(category)}
            displayType={displayType}
            imgIconSrc={getImgIconSrc(category)}
            loading={iconMap.size === 0 || groupIconMap.size === 0}
          />
        ))}
      </div>
    );
  }
}
